import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { userApi } from '../api/client';
import BackToDashboard from '../components/BackToDashboard';

export default function MyProfile() {
  const { t } = useTranslation();
  const { profile, refreshProfile } = useAuth();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<'saved' | 'error' | null>(null);

  useEffect(() => {
    if (profile) {
      setName(profile.name || '');
      setPhone(profile.phone || '');
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await userApi.updateMe({ name: name.trim() || undefined, phone: phone.trim() || undefined });
      await refreshProfile();
      setMessage('saved');
    } catch {
      setMessage('error');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <p style={{ color: '#6b7280' }}>{t('profile.loadingProfile')}</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <BackToDashboard />
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '1rem' }}>{t('profile.title')}</h1>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
        {t('profile.desc')}
      </p>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
          {t('profile.emailReadOnly')}
          <input
            type="email"
            value={profile.email}
            readOnly
            style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              background: '#f9fafb',
              color: '#6b7280',
            }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
          {t('profile.displayName')}
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('profile.yourNamePlaceholder')}
            style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: '1rem',
            }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
          {t('profile.phone')}
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+971..."
            style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: '1rem',
            }}
          />
        </label>
        {message === 'saved' && (
          <div style={{ padding: '0.75rem', background: '#d1fae5', color: '#065f46', borderRadius: 8 }}>
            {t('profile.profileSaved')}
          </div>
        )}
        {message === 'error' && (
          <div style={{ padding: '0.75rem', background: '#fef2f2', color: '#b91c1c', borderRadius: 8 }}>
            {t('dashboard.saveFailed')} {t('profile.tryAgain')}
          </div>
        )}
        <button
          type="submit"
          disabled={saving}
          style={{
            padding: '0.75rem 1.25rem',
            background: saving ? '#9ca3af' : '#0d9488',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? t('profile.saving') : t('profile.save')}
        </button>
      </form>
      <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#6b7280' }}>
        {t('profile.roleLabel')}: <strong>{profile.role === 'admin' ? t('dashboard.admin') : t('dashboard.customer')}</strong>
      </p>
    </div>
  );
}
